import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { api } from '../core/apiClient';

// Types
interface Company {
  id: number;
  name: string;
}

interface Brand {
  id: number;
  name: string;
}

interface Module {
  name: string;
  permissions: {
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyId: number;
  isAdminFullAccess: boolean;
  companies: Company[];
  brands: Brand[];
  modules: Module[];
  role: {
    id: string;
    name: string;
  };
  brandId?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchCompany: (companyId: number) => void;
}

interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface MeResponse {
  user: Omit<User, 'brandId'>;
  brandId?: string;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage keys
const TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';
const COMPANY_ID_KEY = 'erp_company_id';
const COMPANIES_KEY = 'erp_companies';
const BRANDS_KEY = 'erp_brands';
const MODULES_KEY = 'erp_modules';
const IS_ADMIN_KEY = 'erp_is_admin';
const LAST_ACTIVITY_KEY = 'erp_last_activity';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minuts d'inactivitat

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar sesion al cargar
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token) {
        // Si han passat més de 30 min d'inactivitat, no restaurar la sessió
        const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (lastActivity && Date.now() - parseInt(lastActivity, 10) > IDLE_TIMEOUT_MS) {
          [TOKEN_KEY, REFRESH_TOKEN_KEY, COMPANY_ID_KEY, COMPANIES_KEY, BRANDS_KEY, MODULES_KEY, IS_ADMIN_KEY, LAST_ACTIVITY_KEY]
            .forEach(k => localStorage.removeItem(k));
          setIsLoading(false);
          return;
        }
        try {
          const response = await api.get<{ success: boolean; data: MeResponse }>('/auth/me');
          
          if (response.success && response.data) {
            setUser({
              ...response.data.user,
              brandId: response.data.brandId,
            });
          }
        } catch {
          // Si /auth/me falla, intentar restaurar desde localStorage
          try {
            const companyId = localStorage.getItem(COMPANY_ID_KEY);
            const companies = localStorage.getItem(COMPANIES_KEY);
            const brands = localStorage.getItem(BRANDS_KEY);
            const modules = localStorage.getItem(MODULES_KEY);
            
            if (companyId && companies && brands && modules) {
              // Reconstruir user desde localStorage
              setUser({
                id: 'unknown',
                email: 'unknown',
                firstName: 'User',
                lastName: '',
                companyId: parseInt(companyId, 10),
                isAdminFullAccess: localStorage.getItem(IS_ADMIN_KEY) === 'true',
                companies: JSON.parse(companies),
                brands: JSON.parse(brands),
                modules: JSON.parse(modules),
                role: { id: 'unknown', name: 'unknown' },
              });
            } else {
              // Si no hay datos en localStorage, limpiar token
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              setUser(null);
            }
          } catch {
            // Si también falla el fallback, limpiar todo
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setUser(null);
          }
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Refrescar datos del usuario
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: MeResponse }>('/auth/me');
      
      if (response.success && response.data) {
        setUser({
          ...response.data.user,
          brandId: response.data.brandId,
        });
      }
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, []);

  // Login
  const login = useCallback(async (identifier: string, password: string) => {
    setIsLoading(true);

    try {
      const response = await api.post<{ success: boolean; data: { user: User; accessToken: string; refreshToken: string } }>('/auth/login', {
        identifier,
        password,
      });

      console.log('[v0] Login response:', response);
      console.log('[v0] Login response.data:', response.data);

      if (response.success && response.data) {
        const { user: userData, accessToken, refreshToken } = response.data;
        
        console.log('[v0] Login response:', response);
        console.log('[v0] Saving tokens - accessToken:', accessToken, 'refreshToken:', refreshToken);
        
        // Guardar tokens
        localStorage.setItem(TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
        
        // Guardar datos de usuario
        localStorage.setItem(COMPANIES_KEY, JSON.stringify(userData.companies));
        localStorage.setItem(BRANDS_KEY, JSON.stringify(userData.brands));
        localStorage.setItem(MODULES_KEY, JSON.stringify(userData.modules));
        localStorage.setItem(IS_ADMIN_KEY, String(userData.isAdminFullAccess));
        
        // IMPORTANTE: En login, SIEMPRE establecer la empresa activa a la id 1 (Roig i Fills)
        // que es la primera empresa del array (ordenada por id en el backend)
        const defaultCompanyId = userData.companies[0]?.id || 1;
        localStorage.setItem(COMPANY_ID_KEY, String(defaultCompanyId));
        console.log('[v0] Login - set COMPANY_ID to default:', defaultCompanyId);
        
        console.log('[v0] Tokens saved. TOKEN_KEY value:', localStorage.getItem(TOKEN_KEY));
        
        // Actualizar estado con la empresa por defecto
        setUser({
          ...userData,
          companyId: defaultCompanyId,
        });
      }
    } catch (error) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      const message = err.response?.data?.error?.message || 'Error en iniciar sessio';
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      // Intentar cerrar sesion en el servidor
      await api.post('/auth/logout');
    } catch {
      // Ignorar errores de logout
    } finally {
      // Limpiar todo el estado local
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(COMPANY_ID_KEY);
      localStorage.removeItem(COMPANIES_KEY);
      localStorage.removeItem(BRANDS_KEY);
      localStorage.removeItem(MODULES_KEY);
      localStorage.removeItem(IS_ADMIN_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      setUser(null);
    }
  }, []);

  // Timer d'inactivitat: logout automàtic als 30 minuts sense activitat
  useEffect(() => {
    if (!user) return;

    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  // Cambiar empresa activa
  const switchCompany = useCallback((companyId: number) => {
    console.log('[v0] switchCompany - START - changing to companyId:', companyId);
    console.log('[v0] switchCompany - current user:', user);
    
    if (!user) {
      console.error('[v0] switchCompany - no user found');
      return;
    }
    
    // Actualizar localStorage INMEDIATAMENTE
    localStorage.setItem(COMPANY_ID_KEY, String(companyId));
    console.log('[v0] switchCompany - localStorage updated to companyId:', companyId);
    
    // Actualizar el estado INMEDIATAMENTE con el nuevo companyId
    const updatedUser = { ...user, companyId };
    setUser(updatedUser);
    console.log('[v0] switchCompany - user state updated:', updatedUser);
    
    // Luego, en background, obtener los datos de la nueva empresa y recargar
    api.get<{ success: boolean; data: MeResponse }>('/auth/me').then(response => {
      console.log('[v0] switchCompany - /auth/me response received:', response);
      
      if (response.success && response.data) {
        console.log('[v0] switchCompany - updating user with company data:', response.data.user);
        
        // Actualizar localStorage con los nuevos datos
        localStorage.setItem(COMPANY_ID_KEY, String(response.data.user.companyId));
        localStorage.setItem(COMPANIES_KEY, JSON.stringify(response.data.user.companies));
        localStorage.setItem(BRANDS_KEY, JSON.stringify(response.data.user.brands));
        localStorage.setItem(MODULES_KEY, JSON.stringify(response.data.user.modules));
        console.log('[v0] switchCompany - localStorage fully updated');
        
        // Recargar la página para obtener los datos de la nueva empresa
        console.log('[v0] switchCompany - reloading page to fetch new company data');
        window.location.reload();
      }
    }).catch(error => {
      console.error('[v0] switchCompany - error fetching /auth/me:', error);
      // Aún así recargar la página para obtener los datos de la nueva empresa
      console.log('[v0] switchCompany - reloading page despite /auth/me error');
      window.location.reload();
    });
    
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    switchCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export token key for apiClient
export { TOKEN_KEY, REFRESH_TOKEN_KEY };
